import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiDataSource } from '../../utils/api-data-source';
import { ApiRequestService } from '../../shared/api-request.service';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { RBACService } from '../../shared/rbac.service';
import { EmployeeService } from '../employee.service';
import { RbacRolesSelectorComponent } from '../../account/rbac-roles/rbac-roles-selector/rbac-roles-selector.component';
import { RbacRolesTabsComponent } from '../../account/rbac-roles/rbac-roles-tabs/rbac-roles-tabs.component';
import { RbacRole } from '../../interfaces/rbac-role.interface';
import { UserModel } from '../../models/user.model';

@Component({
  selector: 'app-employees-rbac-roles',
  templateUrl: './employees-rbac-roles.component.html',
  styleUrls: ['./employees-rbac-roles.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesRbacRolesComponent implements OnInit, AfterViewInit {
  // The user id (resolved from the loaded employee).
  user_id: number;

  // Columns to show and the data source.
  displayedColumns: string[] = [
    'name',
    'rbac_permissions_count',
    'users_count',
    'actions'
  ];
  dataSource: EmployeesRbacRolesDataSource = new EmployeesRbacRolesDataSource(
    this.app,
    this.api,
    true
  );

  // The paginator and sorter.
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  // RBAC permission flags, resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService,
    public employeeService: EmployeeService
  ) {}

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;

  ngOnInit(): void {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_RBAC_ROLES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // 2. Page-level access: redirect if the user cannot view RBAC roles.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view RBAC roles.'
      );
      this.utils.redirectByLastSegment('details');
      return;
    }

    // Store the user id before making API requests.
    this.user_id = this.employeeService.employee.id;
    this.dataSource.user_id = this.user_id;

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'name',
      'rbac_permissions_count',
      'users_count',
      'actions'
    ];
    if (this.canEdit) {
      this.displayedColumns.unshift('select');
    }

    // 4. Load the data.
    this.dataSource.getData(true);
  }

  ngAfterViewInit(): void {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    // Define the paginator tap events.
    const _tap: any = tap(
      (): void => {
        this.dataSource.limit = this.paginator.pageSize;
        this.dataSource.offset = this.paginator.pageIndex;
        this.dataSource.order_by = this.sort.active;
        this.dataSource.order = this.sort.direction;
        // Refresh the data.
        this.dataSource.getData();
      }
    );

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(_tap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap(
          (sorter: Sort): void => {
            this.dataSource.order = sorter['direction'];
            this.dataSource.getData(true);
          }
        )
      )
      .subscribe();
  }

  /**
   * Whether the employee is a partner user whose roles are read-only to the current user.
   *
   * @returns {boolean}
   */
  get userLocked(): boolean {
    return this.rbac.partnerLockApplies(!!this.dataSource.lastResponse?.isPartnerManaged);
  }

  /**
   * Whether a role row is a partner role that is read-only to the current user.
   *
   * @param role The role row.
   * @returns {boolean}
   */
  roleLocked(role: RbacRole): boolean {
    return this.rbac.partnerLockApplies(role?.partner_user_id != null);
  }

  /**
   * Whether any of the given role ids is a locked partner role on the loaded page.
   *
   * @param rbac_role_ids The role ids to check.
   * @returns {boolean}
   */
  private hitsLockedRole(rbac_role_ids: number[]): boolean {
    return (this.dataSource.dataSubject.value || []).some((role: RbacRole): boolean => {
      return rbac_role_ids.includes(role.id) && this.roleLocked(role);
    });
  }

  /**
   * Edit an existing RBAC role.
   *
   * @param rbac_role_id The id of the RBAC role.
   */
  onEditRbacRole(rbac_role_id?: number): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit RBAC roles.'
      );
      return;
    }

    this.utils
      .showComponentDialog(
        RbacRolesTabsComponent,
        {
          rbac_role_id: rbac_role_id
        },
        {
          width: '800px'
        }
      )
      .then(
        (): void => {
          // Refresh the list after the dialog closed.
          this.dataSource.getData();
        }
      );
  }

  /**
   * Opens the RBAC roles selector dialog and syncs the selected roles with the user.
   *
   * @returns {void}
   */
  onSelectRbacRoles(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee RBAC roles.'
      );
      return;
    }

    // Partner users' roles are managed by support.
    if (this.userLocked) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Show the RBAC roles selector.
    this.utils.showComponentDialog(
      RbacRolesSelectorComponent,
      {
        multiple: true,
        selected: this.dataSource.allIds
      },
      {
        width: '1200px'
      },
      (rbac_role_ids: number[]): void => {
        if (typeof rbac_role_ids !== 'undefined') {
          // Make an update request to the API.
          this.api
            .makeRequest('put', `v2/employee/${this.user_id}/rbac-roles`, {
              rbac_role_ids: rbac_role_ids
            })
            .then(
              (): void => {
                this.utils.showToast(
                  'The selected RBAC role(s) were synced successfully to the user.'
                );
              }
            )
            .catch(
              (_error: any): void => {
                this.utils.handleAPIErrors(_error);
              }
            )
            .finally(
              (): void => {
                this.dataSource.getData(true);
              }
            );
        }
      }
    );
  }

  /**
   * Unlinks an RBAC role from the user.
   *
   * @param rbac_role_id
   * @param evt
   * @return {void}
   */
  onUnlinkRbacRole(rbac_role_id: number, evt: any): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee RBAC roles.'
      );
      return;
    }

    // Partner users' roles and partner roles are managed by support.
    if (this.userLocked || this.hitsLockedRole([rbac_role_id])) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to unlink the RBAC role from the user?',
      [
        {
          text: 'Yes',
          handler: (): void => {
            // Unlink the RBAC role from the user.
            this.unlinkRbacRoles([rbac_role_id]);
          }
        },
        {
          text: 'No',
          handler: (): void => {}
        }
      ]
    );
  }

  /**
   * Unlinks selected RBAC roles from the user.
   *
   * @return {void}
   */
  onUnlinkSelectedRbacRoles(): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee RBAC roles.'
      );
      return;
    }

    // Get the selected RBAC role ids.
    const ids: any[] = this.dataSource.selection.selected;
    // Check if we have RBAC roles selected.
    if (ids.length == 0) {
      this.utils.showModal(
        'Unlink RBAC Roles',
        'Please select some RBAC role(s) to unlink from the user.'
      );
      return;
    }

    // Partner users' roles and partner roles are managed by support.
    if (this.userLocked || this.hitsLockedRole(ids)) {
      this.rbac.showGenericAccessDeniedMessage(this.rbac.partnerManagedMessage());
      return;
    }

    // Make a request to unlink the RBAC roles.
    this.utils.showModal(
      'Unlink RBAC Roles',
      `Are you sure you want to unlink RBAC role(s) from the user?`,
      (): void => {
        this.unlinkRbacRoles(this.dataSource.selection.selected).then(
          (): void => {
            // Clear the list selection.
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * Unlinks the RBAC roles from the user.
   *
   * @param rbac_role_ids
   * @returns {Promise<any>} A promise that resolves when the unlinking is complete.
   * @private
   */
  private unlinkRbacRoles(rbac_role_ids: number[]): Promise<any> {
    // Make an API request to unlink the RBAC roles from the user.
    return this.api
      .makeRequest(
        'delete',
        `v2/employee/${this.user_id}/rbac-roles`,
        {},
        {
          rbac_role_ids: rbac_role_ids.join(',')
        }
      )
      .then(
        (): void => {
          this.utils.showToast('The RBAC role(s) was unlinked from the user.');
          // Refresh the data.
          this.dataSource.getData();
        }
      )
      .catch(
        (_error: any): void => {
          this.utils.handleAPIErrors(_error);
          // Refresh so the UI reflects the unchanged server state.
          this.dataSource.getData();
        }
      );
  }

  /**
   * Get the contact persons of the users associated with a given RBAC role for tooltip display.
   *
   * @param rbac_role The RBAC role for which to fetch users.
   * @returns A string containing the contact persons of the users, separated by new lines.
   */
  getRbacRoleUsersForTooltip(rbac_role: RbacRole): string {
    return (rbac_role.users || [])
      .map((user: UserModel) => {
        return user.contact_person;
      })
      .join('\n');
  }
}

export class EmployeesRbacRolesDataSource extends ApiDataSource {
  // Order and order by params.
  order_by: string = 'name';
  order: string = 'asc';

  // Store the user id.
  user_id: number;

  getData(resetOffset: boolean = false): void {
    this.makeRequest(`v2/employee/${this.user_id}/rbac-roles`, resetOffset, {
      order_by: this.order_by,
      order: this.order
    });
  }
}
